import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import {
  SUPPORTED_UI_LANGUAGES,
  buildLanguageBlock,
} from './i18n/languageCatalog';
import de from './locales/de.json';
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import helpSectionsDe from './locales/helpSections.de';
import helpSectionsEn from './locales/helpSections.en';
import helpSectionsEs from './locales/helpSections.es';
import helpSectionsFr from './locales/helpSections.fr';
import helpSectionsJa from './locales/helpSections.ja';
import helpSectionsPtBR from './locales/helpSections.ptBR';
import helpSectionsZhCN from './locales/helpSections.zhCN';
import ja from './locales/ja.json';
import ptBR from './locales/pt-BR.json';
import zhCN from './locales/zh-CN.json';

type LocaleJson = typeof en;

function mergeHelpSections(base: LocaleJson, sections: typeof helpSectionsEn): LocaleJson {
  return {
    ...base,
    help: {
      ...((base.help ?? {}) as Record<string, unknown>),
      sections,
    },
  } as unknown as LocaleJson;
}

type HelpModule = typeof helpSectionsEn;

const localeBundles: Record<
  string,
  { base: LocaleJson; help: HelpModule }
> = {
  en: { base: en, help: helpSectionsEn },
  de: { base: de, help: helpSectionsDe },
  es: { base: es, help: helpSectionsEs },
  fr: { base: fr, help: helpSectionsFr },
  'pt-BR': { base: ptBR, help: helpSectionsPtBR },
  ja: { base: ja, help: helpSectionsJa },
  'zh-CN': { base: zhCN, help: helpSectionsZhCN },
};

const resources: Record<string, { translation: LocaleJson }> = {};

SUPPORTED_UI_LANGUAGES.forEach((code) => {
  const bundle = localeBundles[code];
  if (bundle) {
    resources[code] = {
      translation: mergeHelpSections(
        { ...bundle.base, language: buildLanguageBlock(code) },
        bundle.help,
      ),
    };
  } else {
    /** English UI via fallbackLng; only the language menu is localized for this code. */
    resources[code] = {
      translation: mergeHelpSections(
        { language: buildLanguageBlock(code) } as LocaleJson,
        helpSectionsEn,
      ),
    };
  }
});

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_UI_LANGUAGES],
    /** Thin locale bundles only override `language`; other keys use `en`. */
    partialBundledLanguages: true,
    /** Map browser locales like es-MX or fr-CA to es / fr bundles. */
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
  })
  .catch(() => {});

export default i18n;
